<script setup>
const { locale } = useI18n()
const location = await useAsyncStoryblok('components/content/map', { version: 'publish', language: locale.value })
const route = useRoute()
const activeTabIndex = ref(location.value.content.components[0].country ? location.value.content.components[0].country : '')
const target = ref(null)
const targetIsVisible = ref(false)

const { stop } = useIntersectionObserver(
  target,
  ([{ isIntersecting }], observerElement) => {
    if (isIntersecting)
      targetIsVisible.value = isIntersecting
  },
)

watch(
  () => route.query.value,
  async () => {
    activeTabIndex.value = route.query.value
    scrollToElement()
  },
)

const filteredLocation = computed(() => {
  return location.value.content.components.filter((location) => {
    return location.country === activeTabIndex.value
  })
})
function tabActive(location) {
  activeTabIndex.value = location
}

function scrollToElement() {
  target.value.scrollIntoView({ behavior: 'smooth' })
}

onMounted(() => {
  activeTabIndex.value = route.query.value ? route.query.value : location.value.content.components[0].country
  location.value.content.components.forEach((element) => {
    if (route.query.value === element.country) {
      setTimeout(() => {
        scrollToElement()
      }, 800)
    }
  })
})
</script>

<template>
  <div ref="target">
    <div class="relative mb-4 w-full items-center rounded-lg flex flex-wrap gap-3" role="tablist">
      <button
        v-for="location in location?.content?.components" :key="location._uid"
        :class="activeTabIndex === location.country ? ' shadow-custom-lg hover:!bg-transparent text-secondary-red-100 [&>span]:!font-600 border-netural-100' : '!border-white'"
        class="overflow-x-auto rounded-md py-3 px-4 text-center transition-all duration-200 ease hover:bg-slate-100 border"
        role="tab" @click="tabActive(location.country)"
      >
        <span v-if="location.country" class="whitespace-nowrap text-sm font-500 2xl:text-md">{{ location.country }}</span>
      </button>
    </div>
    <!-- eslint-disable -->
    <div v-if="filteredLocation?.length" v-for="location in filteredLocation" :key="location._uid" class="border-netural-100 relative flex flex-wrap border rounded-md bg-white p-4 shadow-custom-lg">
      <div class="w-full flex-1">
        <div class="grid gap-x-20">
          <div>
            <h3 v-if="location.name" class="mb-6 text-lg text-primary/90 font-bold leading-[1.4] lg:text-xl">
              {{ location.name }} ({{ location.country }})
            </h3>
            <div class="grid gap-x-4 md:grid-cols-2 md:gap-x-8">
              <div>
                <b class="text-primary font-semibold">Address</b>
                <p v-if="location.address" class="mb-5 text-primary/60 font-medium capitalize">
                  {{ location.address }}
                </p>
                <div class="text-md text-primary/80 font-medium capitalize">
                  <b class="text-primary font-semibold">Phone:</b> {{ location.tel }}
                </div>
                <div v-if="location.email" class="text-md text-primary/80 font-medium">
                  <b class="text-primary font-semibold">Mail:</b> <a class="text-blue-600" :href="`mailto:${location.email}`">{{ location.email }}</a>
                </div>
              </div>
              <div v-if="location.time">
                <div class="mb-5 mt-5 text-md text-primary/80 font-medium xl:mt-0">
                  <b class="text-primary font-semibold">Monday to Friday</b>
                  <p class="mb-0">
                    {{ location.time }}
                  </p>
                </div>
                <div>
                  <b class="text-primary font-semibold">Saturday and Sunday</b>
                  <p class="mb-0">
                    Closed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div  class="hidden min-h-[325px] w-full flex-1 lg:flex">
        <iframe v-if="location.maps_url" class="rounded-md" :src="location.maps_url" :title="`Map of ${location.name || location.country}`" width="100%" height="100%" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade" />
        <div v-else class="bg-slate-100 w-full h-full flex items-center justify-center">
          <p class="font-medium text-slate-500 text-xl">No map available.</p>
        </div>
      </div>
    </div>
  </div>
</template>
